import { Injectable } from '@angular/core';
import { User, UserState, UserType } from '../../store/user.state';
import { BooksService } from '../books.service';
import { NavigationService } from '../navigation.service';
import { SocialLoginService } from './social-login.service';
import { MixpanelService } from '../mixpanel.service';
import { Capacitor } from '@capacitor/core';
import { OneSignalService } from '../onesignal.service';
import { FamilyProfileProvider } from '../../providers/family-profile.provider';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DashboardData, UserProvider } from '../../providers/user.provider';
import { HttpErrorResponse } from '@angular/common/http';
import { UserLocationState } from '../../store/user-location.state';
import { LanguageState } from '../../store/language.state';

/**
 * This service has been created in order to simplify the user.state file
 */
@Injectable({ providedIn: 'root' })
export class UserService {
  dashboardData$: BehaviorSubject<DashboardData | null> = new BehaviorSubject<DashboardData | null>(null);

  constructor(
    private userState: UserState,
    private navigationService: NavigationService,
    private booksService: BooksService,
    private socialLoginService: SocialLoginService,
    private mixpanelService: MixpanelService,
    private oneSignalService: OneSignalService,
    private familyProfileProvider: FamilyProfileProvider,
    private userProvider: UserProvider,
    private userLocationState: UserLocationState,
    private languageState: LanguageState,
  ) {}

  async initialize(params?: { moyaId: string | null; mpesaId: string | null }): Promise<void> {
    if (params?.moyaId) {
      const moyaUser = await firstValueFrom(this.userProvider.loginWithMoya(params.moyaId, this.userLocationState.userLocation$.value.countryCode)).catch(
        (error: HttpErrorResponse) => {
          if (error.status === 404) {
            return this.initialize();
          } else {
            throw error;
          }
        },
      );

      if (moyaUser) {
        await this.userState.initialize(moyaUser);
        return Promise.resolve();
      }
    } else if (params?.mpesaId) {
      const mpesaUser = await firstValueFrom(
        this.userProvider.loginWithMpesa(params.mpesaId, this.userLocationState.userLocation$.value.countryCode, this.languageState.language$.value.selected),
      ).catch((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return this.initialize();
        } else {
          throw error;
        }
      });

      if (mpesaUser) {
        await this.userState.initialize(mpesaUser);
        return Promise.resolve();
      }
    }

    await this.userState.initialize();
    return Promise.resolve();
  }

  async logout(redirectToHome = true): Promise<void> {
    const user = this.userState.user$.value;
    // We only allow logout if the user is not a guest
    if (user.userType !== UserType.GUEST) {
      // We logout the user if logged in with a social authentication method
      void this.socialLoginService.logout(user.userType);

      // We set the guestId & the jwt the user had before he logged in
      if (user.guestCredentials) {
        await this.userState.set({
          userType: UserType.GUEST,
          id: user.guestCredentials.id,
          jwt: user.guestCredentials.jwt,
        });
      } else {
        await this.userState.initialize();
        void this.booksService.initialize();
      }

      this.mixpanelService.reset(); // Reset the user in MixPanel

      if (Capacitor.isNativePlatform()) {
        this.oneSignalService.logout();
      }

      redirectToHome && void this.navigationService.navigate('/home');
    }
  }

  async login(user: User): Promise<void> {
    if (this.userState.user$) {
      user = {
        ...user,
        guestCredentials: {
          id: this.userState.user$.value.id,
          jwt: this.userState.user$.value.jwt,
        },
      };
    }

    await this.userState.set(user);
    await this.booksService.initialize();
    await this.mixpanelService.identify(user.id);
    if (Capacitor.isNativePlatform()) {
      this.oneSignalService.login(user.id);
    }
    void this.syncDashboardData();
  }

  async syncFamilyMetadata(): Promise<void> {
    this.familyProfileProvider.getFamilyMetadata().subscribe(family => void this.userState.set({ familyMetadata: family }));
  }

  async syncDashboardData(): Promise<void> {
    this.userProvider.getDashboardData().subscribe(dashboardData => this.dashboardData$.next(dashboardData));
  }
}
